import { CardContainer } from "@msys/ui";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik, type FormikProps } from "formik";
import { PasswordField } from "../../../../commons/form-fields/PasswordField.js";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "./integrationCardForm.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";

interface PdfTemplate {
  label: string;
  pdfTemplateUrl: string;
  templateIds: string;
}

interface SettingsFormValues {
  pdfTemplates: PdfTemplate[];
  automatedEmailOnProjectSources: string;
  automatedEmailEmailTemplateId: string;
  automatedEmailServiceAccountUserId: string;
  automatedEmailServiceAccountUserPassword: string;
  automatedEmailStandardPdfInclude: boolean;
}

// Per-card form slices (each section card is its own form with its own
// Save/Cancel; a card's save merges its slice over the last saved values,
// so other cards' unsaved edits are never swept along)
type TemplatesValues = Pick<SettingsFormValues, "pdfTemplates">;
type AutoEmailValues = Pick<
  SettingsFormValues,
  | "automatedEmailOnProjectSources"
  | "automatedEmailEmailTemplateId"
  | "automatedEmailServiceAccountUserId"
  | "automatedEmailServiceAccountUserPassword"
  | "automatedEmailStandardPdfInclude"
>;

function pickTemplates(v: SettingsFormValues): TemplatesValues {
  return { pdfTemplates: v.pdfTemplates };
}

function pickAutoEmail(v: SettingsFormValues): AutoEmailValues {
  return {
    automatedEmailOnProjectSources: v.automatedEmailOnProjectSources,
    automatedEmailEmailTemplateId: v.automatedEmailEmailTemplateId,
    automatedEmailServiceAccountUserId: v.automatedEmailServiceAccountUserId,
    automatedEmailServiceAccountUserPassword:
      v.automatedEmailServiceAccountUserPassword,
    automatedEmailStandardPdfInclude: v.automatedEmailStandardPdfInclude,
  };
}

function formValuesToSettings(values: SettingsFormValues) {
  return {
    pdfTemplates: values.pdfTemplates.map(tpl => ({
      label: tpl.label,
      pdfTemplateUrl: tpl.pdfTemplateUrl,
      templateIds: tpl.templateIds
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean),
    })),
    automatedEmailOnProjectSources: values.automatedEmailOnProjectSources
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean),
    automatedEmailEmailTemplateId: values.automatedEmailEmailTemplateId,
    automatedEmailServiceAccountUserId:
      values.automatedEmailServiceAccountUserId,
    automatedEmailServiceAccountUserPassword:
      values.automatedEmailServiceAccountUserPassword,
    automatedEmailStandardPdfInclude: values.automatedEmailStandardPdfInclude,
  };
}

// -- Section card: Templates --

function TemplatesSection({ fp }: { fp: FormikProps<TemplatesValues> }) {
  const { t } = useTranslate("Integrations");
  return (
    <CardContainer title={t("Templates")}>
      <Stack spacing={1.5} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <FieldArray name="pdfTemplates">
          {({ push, remove }) => (
            <>
              {fp.values.pdfTemplates.map((_, i) => (
                <Stack
                  key={i}
                  spacing={1}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t("Template")} {i + 1}
                    </Typography>
                    <IconButton size="small" onClick={() => remove(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    name={`pdfTemplates.${i}.label`}
                    label={t("Label")}
                    size="small"
                  />
                  <TextField
                    name={`pdfTemplates.${i}.pdfTemplateUrl`}
                    label={t("PDF Template URL")}
                    size="small"
                  />
                  <TextField
                    name={`pdfTemplates.${i}.templateIds`}
                    label={t("Template IDs (comma-separated)")}
                    size="small"
                  />
                </Stack>
              ))}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  push({
                    label: "",
                    pdfTemplateUrl: "",
                    templateIds: "",
                  })
                }
                sx={{ alignSelf: "flex-start" }}
              >
                {t("Add template")}
              </Button>
            </>
          )}
        </FieldArray>
        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

// -- Section card: Auto-Email --

function AutoEmailSection({ fp }: { fp: FormikProps<AutoEmailValues> }) {
  const { t } = useTranslate("Integrations");
  return (
    <CardContainer title={t("Auto-Email")}>
      <Stack spacing={1.5} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <TextField
          name="automatedEmailEmailTemplateId"
          label={t("Email Template ID")}
        />
        <TextField
          name="automatedEmailServiceAccountUserId"
          label={t("Service Account User ID")}
        />
        <PasswordField
          name="automatedEmailServiceAccountUserPassword"
          label={t("Service Account Password")}
        />
        <TextField
          name="automatedEmailOnProjectSources"
          label={t("Project Sources (comma-separated)")}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={fp.values.automatedEmailStandardPdfInclude}
              onChange={e =>
                fp.setFieldValue(
                  "automatedEmailStandardPdfInclude",
                  e.target.checked
                )
              }
            />
          }
          label={t("Include standard PDF")}
        />
        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

export const SexyQuotesSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const meta = getIntegrationMeta(integrationId);
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const { anyDirty, dirtyOf } = useCardDirtyRegistry();
  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  const integrationName = meta.sidebarLabel ?? "Sexy Quotes";
  const settings = (orgIntegration?.settings as any) ?? {};

  if (mode === "create") {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName="Sexy Quotes"
        title={`${t("Connect")} Sexy Quotes`}
      >
        <Typography variant="body2" color="text.secondary">
          {t(meta.description as any)}
        </Typography>
        <Button
          variant="contained"
          onClick={() =>
            handleAdd({
              pdfTemplates: [],
              automatedEmailOnProjectSources: [],
              automatedEmailEmailTemplateId: "",
              automatedEmailServiceAccountUserId: "",
              automatedEmailServiceAccountUserPassword: "",
              automatedEmailStandardPdfInclude: false,
            })
          }
        >
          {t("Connect")}
        </Button>
      </SettingsPageLayout>
    );
  }

  const pdfTemplates: PdfTemplate[] = (settings.pdfTemplates ?? []).map(
    (tpl: any) => ({
      label: tpl.label ?? "",
      pdfTemplateUrl: tpl.pdfTemplateUrl ?? "",
      templateIds: (tpl.templateIds ?? []).join(", "),
    })
  );

  // Baseline form values derived from the currently stored settings — each
  // card's save overlays only its own slice on top of these, so other cards'
  // unsaved edits are never swept along and their saved values are kept
  const initialValues: SettingsFormValues = {
    pdfTemplates,
    automatedEmailOnProjectSources: (
      settings.automatedEmailOnProjectSources ?? []
    ).join(", "),
    automatedEmailEmailTemplateId: settings.automatedEmailEmailTemplateId ?? "",
    automatedEmailServiceAccountUserId:
      settings.automatedEmailServiceAccountUserId ?? "",
    automatedEmailServiceAccountUserPassword:
      settings.automatedEmailServiceAccountUserPassword ?? "",
    automatedEmailStandardPdfInclude:
      settings.automatedEmailStandardPdfInclude ?? false,
  };

  const handleSave = async (values: SettingsFormValues) => {
    try {
      await handleUpdate(formValuesToSettings(values));
      // handleUpdate does not refetch — enableReinitialize needs the new
      // stored settings to reset the saved card's form
      await refetchDashboard();
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Settings could not be saved. Please check your input.")
        ),
        { variant: "error" }
      );
    }
  };

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
    >
      <Stack spacing={2}>
        {/* Flowing section cards instead of tabs (2026-07-14 review); each
            card is an independent form with its own Save/Cancel */}
        <Formik<TemplatesValues>
          initialValues={pickTemplates(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("templates")}
              />
              <TemplatesSection fp={fp} />
            </Form>
          )}
        </Formik>

        <Formik<AutoEmailValues>
          initialValues={pickAutoEmail(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("autoEmail")}
              />
              <AutoEmailSection fp={fp} />
            </Form>
          )}
        </Formik>
      </Stack>
    </SettingsPageLayout>
  );
};
